import React from 'react';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Separator } from '../../../ui/separator';
import { Plus, Trash2, X } from 'lucide-react';
import { RATE_TYPES, ADHESIVE_WEIGHTS } from '../../constants';

/**
 * Adhesives / bulk-weight section.
 *
 * Rendered when the parent detects ``isWeightBulkCategory``. Lets the admin
 * enter per-weight rates (with a per-weight rate-type dropdown, and an
 * optional secondary rate for products sold in two units — e.g. per piece
 * AND per bundle for tapes) across all standard adhesive weights AND any
 * ad-hoc custom weights the store carries (e.g. 90GMS, 2LTR).
 */
export function AdhesivesBulkSection({
  adhesiveRates,
  setAdhesiveRates,
  adhesiveSecondaryRates,
  setAdhesiveSecondaryRates,
  setAdhesiveRateTypes,
  rateTypeFor,
  customWeights,
  setCustomWeights,
  newWeight,
  setNewWeight,
  addCustomWeight,
}) {
  return (
    <>
      <Separator />
      <div className="bg-[#F2F4EF] p-3 rounded-lg">
        <Label className="text-xs font-semibold uppercase tracking-wide text-[#556B2F]">
          Enter Rates for Each Weight (Leave empty if not available)
        </Label>

        {/* Weight Section (KG) */}
        <div className="mt-3">
          <p className="text-xs text-[#5C6150] font-medium mb-2 uppercase">Standard Weights (KG)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ADHESIVE_WEIGHTS.map(w => (
              <div key={w.id} className="bg-white p-2 rounded-md border border-[#E2E6D9] space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium w-12 text-[#5C6150] shrink-0">{w.label}</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="₹ Rate"
                    value={adhesiveRates[w.id] || ''}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      setAdhesiveRates(r => ({ ...r, [w.id]: val }));
                    }}
                    className="h-10 text-base font-mono-data flex-1 min-w-[80px]"
                  />
                  <Select
                    value={rateTypeFor(w.id)}
                    onValueChange={v => setAdhesiveRateTypes(r => ({ ...r, [w.id]: v }))}
                  >
                    <SelectTrigger
                      className="h-10 w-24 text-xs shrink-0"
                      data-testid={`adhesive-rate-type-${w.id}`}
                      title="Rate unit"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RATE_TYPES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {/* Secondary rate (optional) — admin toggles this on when
                    an adhesive is sold in 2 different units, e.g. ARGO
                    TAPE per piece AND per bundle. */}
                {adhesiveSecondaryRates[w.id] ? (
                  <div className="flex items-center gap-2 pl-1 border-l-2 border-[#DAA520]/40">
                    <span className="text-[10px] uppercase text-[#DAA520] w-12 shrink-0 font-semibold">+ 2nd</span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="₹ Rate"
                      value={adhesiveSecondaryRates[w.id]?.rate || ''}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        setAdhesiveSecondaryRates(r => ({ ...r, [w.id]: { ...r[w.id], rate: val } }));
                      }}
                      className="h-8 text-sm font-mono-data flex-1 min-w-[80px]"
                      data-testid={`adhesive-secondary-rate-${w.id}`}
                    />
                    <Select
                      value={adhesiveSecondaryRates[w.id]?.rateType || 'per bundle'}
                      onValueChange={v => setAdhesiveSecondaryRates(r => ({ ...r, [w.id]: { ...r[w.id], rateType: v } }))}
                    >
                      <SelectTrigger
                        className="h-8 w-24 text-xs shrink-0"
                        data-testid={`adhesive-secondary-rate-type-${w.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RATE_TYPES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => setAdhesiveSecondaryRates(r => { const n = { ...r }; delete n[w.id]; return n; })}
                      className="text-[#B93838] hover:bg-red-50 rounded p-1"
                      aria-label="Remove 2nd rate"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAdhesiveSecondaryRates(r => ({ ...r, [w.id]: { rate: '', rateType: 'per bundle' } }))}
                    className="text-[10px] text-[#DAA520] hover:underline ml-12"
                    data-testid={`adhesive-add-secondary-${w.id}`}
                  >
                    + Add 2nd rate (e.g. per bundle)
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Custom Weights/Volumes Section - Always show */}
        <div className="mt-4 pt-3 border-t border-[#E2E6D9]">
          <p className="text-xs text-[#DAA520] font-medium mb-2 uppercase">
            Custom Weights/Volumes (GMS, LTR, etc.)
          </p>

          {customWeights.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              {customWeights.map(w => (
                <div key={w} className="flex items-center gap-2 bg-white p-2 rounded-md border border-[#DAA520]/50">
                  <span className="text-sm font-medium text-[#DAA520] shrink-0 w-12 truncate" title={w}>{w}</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="₹ Rate"
                    value={adhesiveRates[w] || ''}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      setAdhesiveRates(r => ({ ...r, [w]: val }));
                    }}
                    className="h-10 text-base font-mono-data flex-1 min-w-[80px]"
                  />
                  <Select
                    value={rateTypeFor(w)}
                    onValueChange={v => setAdhesiveRateTypes(r => ({ ...r, [w]: v }))}
                  >
                    <SelectTrigger
                      className="h-10 w-24 text-xs shrink-0"
                      data-testid={`adhesive-custom-rate-type-${w}`}
                      title="Rate unit"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RATE_TYPES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-[#B93838] shrink-0"
                    onClick={() => {
                      setCustomWeights(c => c.filter(x => x !== w));
                      setAdhesiveRates(r => {
                        const newR = { ...r };
                        delete newR[w];
                        return newR;
                      });
                      setAdhesiveRateTypes(r => {
                        const newR = { ...r };
                        delete newR[w];
                        return newR;
                      });
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#8F9485] mb-3">No custom weights added yet</p>
          )}

          {/* Add custom weight input */}
          <div className="flex items-center gap-2">
            <Input
              placeholder="Add: 90GMS, 180GMS, 2LTR, 5LTR..."
              value={newWeight}
              onChange={e => setNewWeight(e.target.value.toUpperCase())}
              className="h-10 text-sm flex-1"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomWeight();
                }
              }}
            />
            <Button type="button" variant="outline" size="sm" onClick={addCustomWeight} className="h-10 shrink-0">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          <p className="text-[10px] text-[#8F9485] mt-1">
            Format: 90GMS, 180GMS, 450GMS, 1.8KG, 2LTR, 5LTR
          </p>
        </div>
      </div>
    </>
  );
}
